"use client"
import { useState, useEffect } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { Button } from "@/components/ui/button";
import Lottie from "lottie-react";
import clockAnimation from "@animation/clock.json";
import contactAnimation from "@animation/comms.json";
import { scrollToSellWatchForm, navigateToContact } from "@/lib/scroll-utils";
import { motion, AnimatePresence, easeIn, easeOut, easeInOut } from "framer-motion";
import { useSmoothScroll } from "@/hooks/useSmoothScroll";
import { useDialog } from "@/contexts/dialog-context";

// Animation variants for hamburger menu
const topLineVariants = {
  open: {
    width: 0,
    transition: { duration: 0.1, easeIn },
  },
  close: {
    width: "100%",
    transition: { duration: 0.1, delay: 0.3, easeOut },
  },
};

const middleLineVariants = {
  open: {
    rotate: 135,
    transition: { duration: 0.3, delay: 0.1, easeInOut },
  },
  close: {
    rotate: 0,
    transition: { duration: 0.3, easeInOut },
  },
  openSecond: {
    rotate: 45,
    transition: { duration: 0.3, delay: 0.1, easeInOut },
  },
  closeSecond: {
    rotate: 0,
    transition: { duration: 0.3, easeInOut },
  },
};

const bottomLineVariants = {
  open: {
    width: 0,
    transition: { duration: 0.1, easeIn },
  },
  close: {
    width: "100%",
    transition: { duration: 0.1, delay: 0.3, easeOut },
  },
};

const mobileNavVariants = {
  open: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, easeInOut },
  },
  close: {
    opacity: 1,
    y: "-100%",
    transition: { duration: 0.4, easeInOut },
  },
};

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false)
  const [isVisible, setIsVisible] = useState(false)
  const pathname = usePathname()
  const { scrollToSection } = useSmoothScroll()
  const { openDialog } = useDialog()
  
  const toggleMobileMenu = () => setIsOpen((prev) => !prev)
  
  const handleNavClick = (sectionId: string) => {
    if (isOpen) setIsOpen(false)
    scrollToSection(sectionId, { targetPage: '/', offset: -120 })
  }
  
  const handleFormCTAClick = () => {
    if (pathname === '/contact') {
      openDialog()
    } else {
      scrollToSellWatchForm(() => openDialog())
    }
  }
  
  const handleMobileNavClick = () => {
    setIsOpen(false)
  }
  
  // Control sticky bottom button visibility
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY
      const windowHeight = window.innerHeight
      const documentHeight = document.body.scrollHeight
      
      const threshold = windowHeight * 0.8 // 10vh
      const buffer = 100 // Hide 100px before reaching bottom
      const isNearBottom = scrollY + windowHeight >= documentHeight - buffer
      
      setIsVisible(scrollY > threshold && !isNearBottom)
    }
    
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])
  
  return (
    <>
    <nav className="fixed top-0 flex flex-col w-full justify-center items-center border-b border-foreground/20 bg-background shadow-sm" style={{zIndex: 1000}}>
      {/* Top bar - bg=foreground, text=background */}
      <div className="flex justify-center items-start bg-foreground text-background wrapper h-8">
        <div className="flex max-w-screen-2xl w-full text-sm tracking-wide font-medium justify-between">
          <span>Same Day Payments</span>
          <span>15+ Years Experience</span>
        </div>
      </div>
      
      {/* Main navbar - lighter background with flex */}
      <div className="flex w-full bg-background/0 px-[2.5%] md:px-[5%] py-4 h-18">
        <div className="w-full flex items-center justify-between">
          {/* Logo/Brand - left */}
          <Link 
            href="/" 
            onClick={handleMobileNavClick}
            className="text-base leading-4 font-semibold md:text-h5 text-foreground hover:text-[oklch(21.2%_0.055_137.8)] transition-colors"
          >
            WeBuyYour<br />Watches.com
          </Link>
          
          {/* Desktop CTAs - right */}
          <div className="hidden md:flex items-center gap-4">
            <Button
              onClick={navigateToContact}
              className="bg-transparent text-foreground border border-foreground hover:bg-transparent rounded-3xl" 
            >
              Get in touch
              <div className="rounded-full aspect-square border border-foreground bg-background/90 w-7 -mr-3 flex items-center justify-center overflow-hidden">
                <Lottie 
                  animationData={contactAnimation}
                  loop={true}
                  className="w-5 h-5"
                />
              </div>
            </Button>

            <Button
              onClick={handleFormCTAClick}
              className="bg-cta text-background rounded-3xl"
            >
              Sell Your Watch
              <div className="rounded-full aspect-square bg-background/90 w-7 -mr-3 flex items-center justify-center overflow-hidden">
                <Lottie 
                  animationData={clockAnimation}
                  loop={true}
                  className="w-5 h-5"
                />
              </div>
            </Button>
          </div>
          
          {/* Hamburger Menu - mobile only */}
          <div className="relative z-50 flex items-center gap-4 md:hidden">
            <button
              className="flex size-12 flex-col items-center justify-center justify-self-end"
              onClick={toggleMobileMenu}
              aria-label={isOpen ? "Close menu" : "Open menu"}
            >
              <span className="relative flex size-6 flex-col items-center justify-center">
                <motion.span
                  className="absolute top-[3px] h-0.5 w-full bg-current"
                  animate={isOpen ? "open" : "close"}
                  variants={topLineVariants}
                />
                <motion.span
                  className="absolute h-0.5 w-full bg-current"
                  animate={isOpen ? "open" : "close"}
                  variants={middleLineVariants}
                />
                <motion.span
                  className="absolute h-0.5 w-full bg-current"
                  animate={isOpen ? "openSecond" : "closeSecond"}
                  variants={middleLineVariants}
                />
                <motion.span
                  className="absolute bottom-[3px] h-0.5 w-full bg-current"
                  animate={isOpen ? "open" : "close"}
                  variants={bottomLineVariants}
                />
              </span>
            </button>
          </div>
        </div>
      </div>
    </nav>
    
    {/* Mobile Menu Overlay */}
    <AnimatePresence>
      {isOpen && (
        <motion.div 
          className="fixed top-24 bottom-0 inset-x-0 w-full bg-foreground text-background md:hidden overflow-hidden z-50"
          animate={isOpen ? "open" : "close"}
          initial="close"
          exit="close"
          variants={mobileNavVariants}
          transition={{ duration: 0.2 }}
        >
          <motion.div
            variants={{
              open: { opacity: 1 },
              close: { opacity: 0 },
            }}
            animate={isOpen ? "open" : "close"}
            initial="close"
            exit="close"
            transition={{ duration: 0.2 }}
            className="min-h-fit h-full pt-8 flex flex-col overflow-y-auto"
          >
            <div className="flex flex-col justify-between items-start h-full w-full gap-y-4 py-2 md:gap-y-16 flex-none pt-4">
              {/* Navigation Menu Items */}
              <div className="flex flex-col justify-between gap-x-10 gap-y-6 px-[5%] pt-4 md:pt-8 h-full  w-full">
                <button
                  onClick={() => handleNavClick('home')}
                  className="text-2xl font-medium md:text-3xl w-full text-left hover:opacity-80 transition-opacity"
                >
                  Home
                </button>
                
                <button
                  onClick={() => handleNavClick('our-process')}
                  className="text-2xl font-medium md:text-3xl w-full text-left hover:opacity-80 transition-opacity"
                >
                  Our Process
                </button>
                
                <button
                  onClick={() => handleNavClick('faq')}
                  className="text-2xl font-medium md:text-3xl w-full text-left hover:opacity-80 transition-opacity"
                >
                  FAQ
                </button>
                
                <button
                  onClick={() => handleNavClick('testimonials')}
                  className="text-2xl font-medium md:text-3xl w-full text-left hover:opacity-80 transition-opacity"
                >
                  Testimonials
                </button>
                
                <div className="h-[1px] w-full bg-background rounded-full opacity-20"></div>
                
                <Link
                  href="/contact"
                  onClick={handleMobileNavClick}
                  className="text-2xl font-medium md:text-3xl w-full text-left hover:opacity-80 transition-opacity"
                >
                  Contact Us
                </Link>
                
                <button
                  onClick={() => {
                    handleMobileNavClick()
                    handleFormCTAClick()
                  }}
                  className="text-2xl font-medium md:text-3xl w-full text-left hover:opacity-80 transition-opacity"
                >
                  Sell Your Watch
                </button>
                
                
                {/* Contact Information */}
                <div className="flex flex-col gap-y-4 h-full justify-end pb-8">
                <div className="h-[1px] w-full bg-background rounded-full opacity-20"></div>
                  <p className="text-lg font-medium">Get in touch today for</p>
                  <div className="text-sm space-y-1 opacity-80">
                    <p>Same Day Payments</p>
                    <p>15+ Years Experience</p>
                    <p>Professional Watch Valuations</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
    
    {/* Sticky Bottom Button */}
    {pathname !== "/contact" && (
      <motion.div 
        className="fixed bottom-4 right-0 z-40 w-full h-fit flex items-center justify-center px-[2.5%]"
        initial={{ y: 100 }}
        animate={isVisible ? { y: 0 } : { y: 100 }}
        transition={{ duration: 0.8, delay: 0.8, ease: "anticipate" }}
      >
        <div className="bg-foreground rounded-3xl p-2 border-background/20 border text-center flex flex-col sm:flex-row items-center justify-center gap-2 md:gap-4 w-fit sm:w-fit shadow-lg">
          <Button
            onClick={openDialog}
            className="cursor-pointer w-fit sm:w-auto bg-cta/90 border border-background/30 text-background hover:bg-cta/90 rounded-3xl"
          >
            Sell Your Watch
            <div className="rounded-full aspect-square bg-background/90 w-7 -mr-3 flex items-center justify-center overflow-hidden">
              <Lottie 
                animationData={clockAnimation}
                loop={true}
                className="w-5 h-5"
              />
            </div>
          </Button>
        </div>
      </motion.div>
    )}
    
    </>
  );
}
